import {
  alignMap,
  gapMap,
  gapMdMap,
  gapXlMap,
  justifyMap,
} from "@/constants/designSystemScale";
import {
  GapValuesType,
  JustifyType,
  AlignType,
  GapMdValuesType,
  GapXlValuesType,
} from "@/types/designSystem.types";
import { forwardRef, MouseEventHandler } from "react";

interface StackProps {
  // CSS Grid with gap provides better cross-browser compatibility than Flexbox gap (flex gap not supported on Tizen <=6.5)
  useGrid?: boolean;
  direction: "row" | "column";
  gap?: GapValuesType;
  gapMd?: GapMdValuesType;
  gapXl?: GapXlValuesType;
  justify?: JustifyType;
  align?: AlignType;
  wrap?: boolean;
  fullWidth?: boolean;
  fullHeight?: boolean;
  className?: string;
  children: React.ReactNode;
  dataTestid?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
}

const Stack = forwardRef<HTMLDivElement, StackProps>(
  (
    {
      useGrid = false,
      direction,
      gap = 0,
      gapMd,
      gapXl,
      justify = "start",
      align = "start",
      wrap = true,
      fullWidth = false,
      fullHeight = false,
      // TODO: classname should be removed once Dialect Selector is written with DS
      className,
      dataTestid,
      children,
      onClick,
    },
    ref,
  ) => {
    const gridFlowClass =
      direction === "column" ? "grid-flow-row" : "grid-flow-col";
    const flexDirectionClass = direction === "column" ? "flex-col" : "flex-row";

    const gapClass = gapMap[gap].class;
    const justifyClass = justifyMap[justify].class;
    const alignClass = alignMap[align].class;
    const wrapClass = wrap ? "flex-wrap" : "flex-nowrap";
    const widthClass = fullWidth ? "w-full" : "w-auto";
    const heightClass = fullHeight ? "h-full" : "h-auto";
    const gapMdClass = gapMd ? gapMdMap[gapMd].class : "";
    const gapXlClass = gapXl ? gapXlMap[gapXl].class : "";

    const classes =
      `${useGrid ? `grid ${gridFlowClass} ` : `flex ${flexDirectionClass} `} ${gapClass} ${justifyClass} ${alignClass} ${wrapClass} ${widthClass} ${heightClass} ${gapMdClass} ${gapXlClass}` +
      (className ? ` ${className}` : "");

    return (
      <div
        ref={ref}
        data-testid={dataTestid}
        onClick={onClick}
        className={classes}
      >
        {children}
      </div>
    );
  },
);

Stack.displayName = "Stack";

export const Row = forwardRef<HTMLDivElement, Omit<StackProps, "direction">>(
  (props, ref) => <Stack ref={ref} direction="row" {...props} />,
);

Row.displayName = "Row";

export const Column = forwardRef<HTMLDivElement, Omit<StackProps, "direction">>(
  (props, ref) => <Stack ref={ref} direction="column" {...props} />,
);

Column.displayName = "Column";
